import { useMemo } from 'react'
import { IssueTable, TableSkeleton } from '@/components/tables/issue-table'
import { ROLLOUT_COLUMNS } from '@/components/tables/issue-columns'
import { ViewHeader } from '@/components/shell/view-header'
import { SortMenu } from '@/components/common/sort-menu'
import { useViewFilters } from '@/hooks/use-view-filters'
import { useClosedIssues, useIssues } from '@/lib/query'
import { ViewError } from '@/components/common/query-states'
import { isRollout } from '@/lib/stage'
import { sortIssues } from '@/lib/sort'
import { useSortPreference } from '@/hooks/use-sort-preference'
import type { WorkIssue } from '@/types/github'
import { ROLLOUTS_VIEW } from '@/lib/views'

// Rollouts: changes that ship code which executes *after* deploy and must run
// to completion across every production subscription before we trust them —
// today system/datafix, soon system/migration (labels are bot-applied; see
// n3oltd/work). Open = still running or needs attention; closed = verified,
// carrying the bot's completion report. We pull both so verification can
// confirm a rollout actually landed, not just that it stopped failing.
export default function Rollouts() {
  const open = useIssues()
  const closed = useClosedIssues()
  const chipFilter = useViewFilters()
  const [sort] = useSortPreference()

  const items = useMemo(() => {
    // Dedup by id: an issue closed mid-poll (e.g. by the bot) can briefly sit
    // in both caches. Insert open first, then closed, so the closed copy wins —
    // it carries closedAt and the verified completion report.
    const byId = new Map<string, WorkIssue>()
    for (const i of ROLLOUTS_VIEW.pool(open.data ?? [], {})) byId.set(i.id, i)
    for (const i of closed.data ?? []) if (isRollout(i.labels)) byId.set(i.id, i)
    return sortIssues(chipFilter.apply([...byId.values()]), sort)
  }, [open.data, closed.data, chipFilter, sort])

  const isError = open.isError || closed.isError
  const error = open.error ?? closed.error
  const isPending = open.isPending || closed.isPending
  const retry = () => {
    open.refetch()
    closed.refetch()
  }

  return (
    <div className="flex h-full flex-col">
      <ViewHeader
        title="Rollouts"
        meta={
          <span className="font-mono text-[11px]">
            label:system/datafix,system/migration · open = running · closed = verified (≤50
            recent) · {items.length} items
          </span>
        }
        // Bot-generated issues carry no client/product/priority, but a failed
        // rollout gets an assignee (whoever's driving it to done) — so the useful
        // facets are state (open vs verified) and assignee. Default shows all.
        chips={['is', 'assignee']}
        toolbar={<SortMenu />}
      />
      {isError ? (
        <ViewError what="rollouts" error={error} onRetry={retry} />
      ) : isPending ? (
        <TableSkeleton />
      ) : (
        <IssueTable
          issues={items}
          bulkKind="rollout"
          columns={ROLLOUT_COLUMNS}
          emptyTitle="No rollouts"
          emptyHint="No issues carry a system/datafix or system/migration label yet."
        />
      )}
    </div>
  )
}
